import type {MouseEvent, ReactElement} from 'react';
import React from 'react';
import type {PromptSubfilter} from '@Panels/ai-voice-panel/ai-voice-panel.types';
import {PromptSubfilterType} from '@Panels/ai-voice-panel/ai-voice-panel.types';
import {Icon} from '@Components/icon-v2';
import {Text, TextSize} from '@Components/text';
import {IconSize} from '@Components/icon-v2/icon.types';
import {CircularProgressLoader, LOADER_SIZE} from '@Components/circular-progress-loader';
import {PMW_COLORS_NEUTRAL} from '@Utils/color.util';
import AudioItemWave from '@Components/base-grid/components/audio-item-wave/audio-item-wave';
import styles from './ai-voice-prompt-filter-popup.module.scss';

export interface AiVoicePromptFilterPopupProps {
  subfilters: PromptSubfilter[];
  selectedSubfilter?: number;
  currentTrack?: string;
  loadingTrack?: string;
  onFilterClicked(id: number, event: MouseEvent): void;
  onTrackPlay(trackSrc: string): void;
  onTrackPause(trackSrc: string): void;
  className?: string;
}

export default function AiVoicePromptFilterPopup({...props}: AiVoicePromptFilterPopupProps): ReactElement {
  const getPlayablePill = (filterId: number, name: string, track: string): ReactElement => {
    return (
      <div key={filterId} className={`flex-center ${styles.playablePillContainer}`}>
        <Icon
          className="_cursor-pointer content-body"
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            if (props.currentTrack && props.currentTrack === track) {
              props.onTrackPause(track);
            } else {
              props.onTrackPlay(track);
            }
          }}
          icon={props.currentTrack && props.currentTrack === track ? 'icon-pause' : 'icon-play-video'}
          size={IconSize.SIZE_ICON_16}
        />
        <Text className="content-body" bold size={TextSize.XSMALL} disableSelection val={name} />
        {getActivityIndicator(track)}
      </div>
    );
  };

  const getActivityIndicator = (track: string): ReactElement | null => {
    if (props.loadingTrack && props.loadingTrack === track) {
      return <CircularProgressLoader size={LOADER_SIZE.XSMALL} />;
    }
    if (props.currentTrack && props.currentTrack === track) {
      return <AudioItemWave isAnimating width={16} height={16} color={PMW_COLORS_NEUTRAL.NEUTRAL_70} totalBars={4} />;
    }
    return null;
  };

  return (
    <ul className={`_full-width  flexbox flex-wrap spacing-p-0 spacing-m-0 ${styles.listContainer} ${props.className ?? ''}`}>
      {props.subfilters.map((subfilters): ReactElement => {
        const isSelected = props.selectedSubfilter && props.selectedSubfilter === subfilters.id;
        return (
          <li
            key={subfilters.id}
            className={`${styles.listItemContainer} spacing-p-t-2 spacing-p-b-2 spacing-p-r-3 spacing-p-l-3 radius-round _cursor-pointer ${isSelected ? styles.selectedPill : ''}`}
            onClick={(e) => {
              props.onFilterClicked(subfilters.id, e);
            }}
          >
            {subfilters.type === PromptSubfilterType.PLAYABLE ? getPlayablePill(subfilters.id, subfilters.name, subfilters.soundtrack) : subfilters.name}
          </li>
        );
      })}
    </ul>
  );
}
